import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { treatmentPlanService } from "../../services/treatmentPlan.service";
import { TreatmentPlanRequest } from "../../domain/treatmentPlan.interface";

export const useGetTreatmentPlans = (treatmentPlanId?: string) => {
	return useQuery({
		queryKey: ["sims-treatment-plans", treatmentPlanId],
		queryFn: () => treatmentPlanService.getTreatmentPlans(treatmentPlanId),
		staleTime: 1000 * 60 * 5,
	});
};

export const useGetTreatmentPlan = (treatmentPlanId: string) => {
	const query = useQuery({
		queryKey: ["sims-treatment-plan", treatmentPlanId],
		queryFn: () => treatmentPlanService.getTreatmentPlan(treatmentPlanId),
		enabled: !!treatmentPlanId,
		staleTime: 1000 * 60 * 5,
	});

	return {
		...query,
		refetch: query.refetch,
	};
};

export const useCreateTreatmentPlan = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (treatmentPlan: TreatmentPlanRequest) =>
			treatmentPlanService.createTreatmentPlan(treatmentPlan),
		onSuccess: () => {
			// Invalidate and refetch treatment plans queries
			queryClient.invalidateQueries({
				queryKey: ["sims-treatment-plans"],
			});
		},
	});
};

export const useUpdateTreatmentPlan = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: TreatmentPlanRequest }) =>
			treatmentPlanService.updateTreatmentPlan(id, data),
		onSuccess: () => {
			// Invalidate and refetch treatment plans queries
			queryClient.invalidateQueries({
				queryKey: ["sims-treatment-plans"],
			});
			queryClient.invalidateQueries({
				queryKey: ["sims-treatment-plan"],
			});
		},
	});
};

export const useDeleteTreatmentPlan = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (treatmentPlanId: string) =>
			treatmentPlanService.deleteTreatmentPlan(treatmentPlanId),
		onSuccess: () => {
			// Invalidate and refetch treatment plans queries
			queryClient.invalidateQueries({
				queryKey: ["sims-treatment-plans"],
			});
		},
	});
};
